import { useEffect, useRef } from 'react';

/**
 * Attaches translateY parallax. `factor` is multiplied by scrollY.
 * Disengages once the host has fully scrolled past viewport.
 */
export function useParallax<T extends HTMLElement = HTMLElement>(factor = 0.4) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = window.scrollY || 0;
        const vh = window.innerHeight;
        if (y > vh) return;
        el.style.transform = `translate3d(0, ${(y * factor).toFixed(1)}px, 0)`;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [factor]);
  return ref;
}
